import styled from "styled-components";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 640px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  justify-content: center;
`;

const Tab = styled.a`
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Mission card (gradient) ── */
const ContentSection = styled.section`
  padding: clamp(32px, 4vw, 48px) 0 clamp(80px, 10vw, 120px);
`;

const MissionCard = styled.div`
  background-color: #4f46e5;
  background-image:
    linear-gradient(135deg, rgba(79, 70, 229, 0.72) 0%, rgba(124, 58, 237, 0.82) 100%),
    url("/images/super1.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 28px;
  padding: clamp(48px, 7vw, 88px) clamp(28px, 5vw, 56px);
  color: #ffffff;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 25% 20%,
      rgba(255, 255, 255, 0.22),
      transparent 60%
    );
    pointer-events: none;
  }
`;

const MissionEyebrow = styled.p`
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 18px;
  position: relative;
`;

const MissionTitle = styled.h2`
  font-size: clamp(28px, 4vw, 46px);
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -0.025em;
  margin: 0 0 22px;
  position: relative;
`;

const MissionLead = styled.p`
  font-size: clamp(15.5px, 1.4vw, 17.5px);
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.94);
  margin: 0 auto;
  max-width: 680px;
  position: relative;
`;

/* ── Product card (flagship) ── */
const ProductCard = styled.section`
  margin-top: clamp(40px, 5vw, 64px);
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: clamp(32px, 5vw, 56px);
  background: #ffffff;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: clamp(28px, 4vw, 56px);
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ProductVisual = styled.div`
  aspect-ratio: 1;
  border-radius: 20px;
  background: linear-gradient(135deg, #ecfeff 0%, #eef2ff 60%, #f5f3ff 100%);
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, 0.55),
      transparent 60%
    );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    max-width: 220px;
    margin: 0 auto;
  }
`;

const ProductTag = styled.span`
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6366f1;
  background: #eef2ff;
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 14px;
`;

const ProductName = styled.h3`
  font-size: clamp(24px, 2.6vw, 32px);
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const ProductDesc = styled.p`
  font-size: 15.5px;
  line-height: 1.75;
  color: #64748b;
  margin: 0;
`;

/* ── Stats row ── */
const Stats = styled.div`
  margin-top: clamp(40px, 5vw, 64px);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  text-align: center;
  padding: 28px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #fafbff;

  .num {
    font-size: clamp(26px, 3.2vw, 38px);
    font-weight: 700;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #6366f1, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.1;
  }
  .label {
    font-size: 13.5px;
    color: #64748b;
    margin-top: 8px;
  }
`;

/* ── Icons ── */
const BrainIcon = () => (
  <svg
    width="96"
    height="96"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ position: "relative", zIndex: 1 }}
  >
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
  </svg>
);

const TABS = [
  { key: "greetings", label: "Greetings", href: "/aboutUs/greetings" },
  { key: "vision", label: "Vision", href: "/aboutUs/vision" },
  { key: "history", label: "History", href: "/aboutUs/history" },
  { key: "map", label: "Map", href: "/aboutUs/map" },
];

export function Greetings() {
  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Company</Eyebrow>
          <HeroTitle>Greetings</HeroTitle>
          <HeroLead>
            Youngand is an AI-based digital health startup developing
            innovative solutions for cognitive health support.
          </HeroLead>
          <Tabs>
            {TABS.map((t) => (
              <Tab key={t.key} href={t.href} $active={t.key === "greetings"}>
                {t.label}
              </Tab>
            ))}
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <MissionCard>
            <MissionEyebrow>Our mission</MissionEyebrow>
            <MissionTitle>
              Innovative solutions for cognitive health support
            </MissionTitle>
            <MissionLead>
              Through AI-based digital health, we help seniors lead better
              lives — combining cognitive engagement with physical movement to
              support healthier aging at home and in the community.
            </MissionLead>
          </MissionCard>

          <ProductCard>
            <ProductVisual>
              <BrainIcon />
            </ProductVisual>
            <div>
              <ProductTag>Flagship product</ProductTag>
              <ProductName>Brain Health Playground</ProductName>
              <ProductDesc>
                A digital training platform that monitors early signs of
                cognitive decline through motion, voice, and facial
                recognition — then delivers personalized cognitive-physical
                exercise. We are actively expanding into Singapore through
                international research collaboration.
              </ProductDesc>
            </div>
          </ProductCard>

          <Stats>
            <Stat>
              <div className="num">2022</div>
              <div className="label">Founded in Gwangju</div>
            </Stat>
            <Stat>
              <div className="num">15+</div>
              <div className="label">Government &amp; accelerator programs</div>
            </Stat>
            <Stat>
              <div className="num">KR · SG</div>
              <div className="label">Active markets</div>
            </Stat>
          </Stats>
        </Container>
      </ContentSection>
    </Page>
  );
}
